import classNames from "classnames";
import React, { useCallback, useId } from "react";

import { useIsClientSide } from "@utils/hooks/isClient.hook";
import { formatPrice } from "@utils/productPrice";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { Input } from "@components/General/Form/input.component";
import { Tooltip } from "@components/General/Tooltip/tooltip.component";
import { TooltipType } from "@components/General/Tooltip/tooltip.types";

import { useLanguage } from "@context/language.context";

import {
    AdditionalServiceModel,
    ProductDocument,
} from "lib/@types/service/shared";

import "./additionalService.scss";

type AdditionalServiceComponentProps = {
    additionalService: AdditionalServiceModel;
    tooltip?: TooltipType;
    type?: "checkbox" | "static";
    removable?: boolean;
    addable?: boolean;
    showPrice?: boolean;
    onClick?: (additionalService: AdditionalServiceModel) => void;
    isSelected?: boolean;
    documents?: Array<ProductDocument>;
};

const AdditionalServiceComponent = ({
    additionalService,
    tooltip,
    type = "static",
    removable = true,
    showPrice = false,
    onClick,
    isSelected,
    documents = [],
    addable = true,
}: AdditionalServiceComponentProps) => {
    const { t } = useGranite();
    const isClientSide = useIsClientSide();
    const inputId = useId();
    const { getLocale } = useLanguage();

    const disabled = (!isSelected && !addable) || (isSelected && !removable);

    const handleOnServiceProductClick = () =>
        onClick && onClick(additionalService);

    const getPdpHref = useCallback(
        () =>
            isClientSide
                ? `${window.origin}/${getLocale(true)}/p/${
                      additionalService.sku
                  }`
                : undefined,
        [isClientSide],
    );

    const labelContent = (
        <>
            {additionalService.title}
            {showPrice && additionalService.price && (
                <span> | {formatPrice(additionalService.price)}</span>
            )}
            {type === "checkbox" && (
                <span className={"ml-1 small font-italic"}>
                    ({t("general.cartItem.serviceProduct.label.optional")})
                </span>
            )}
            {tooltip?.title && tooltip?.text && (
                <Tooltip title={tooltip.title} innerHTML={tooltip.text} />
            )}
        </>
    );

    return (
        <div className={classNames(disabled && "additionalService--disabled")}>
            {type === "static" && <p className={"m-0 mt-2"}>{labelContent}</p>}
            {type === "checkbox" && (
                <div>
                    <Input
                        type="checkbox"
                        id={inputId}
                        name={additionalService.sku}
                        value={additionalService.sku}
                        checked={isSelected}
                        onChange={handleOnServiceProductClick}
                        disabled={disabled}
                    />
                    <label className={"m-0"} htmlFor={inputId}>
                        {labelContent}
                    </label>
                </div>
            )}
            <p className="additionalService__description">
                {t("general.cartItem.serviceProduct.supplierContact")}
                {isClientSide && (
                    <a className={"ml-1"} href={getPdpHref()}>
                        {t("general.cartItem.serviceProduct.moreInfo")}
                    </a>
                )}
            </p>
            {documents.map((doc, index) => (
                <p
                    key={`doc-${index}`}
                    className="additionalService__documents"
                >
                    <a href={doc.url} title={doc.altText || ""} target="_blank">
                        {t("general.ecommerce.additionalServices.doc")}
                    </a>
                </p>
            ))}
        </div>
    );
};

export { AdditionalServiceComponent };
